import React from 'react';
import { Link } from 'react-router-dom';

const Icon = ({ d }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const features = [
  { t: "OAuth 2.0 Authentication", d: "Secure QuickBooks connection with automatic token refresh built in.", i: "M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z" },
  { t: "Multi-tenant Support", d: "Manage multiple QuickBooks organizations per user out of the box.", i: "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75" },
  { t: "Full Accounting API", d: "Invoices, bills, payments, payroll, journal entries and more.", i: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" },
  { t: "Financial Reports", d: "P&L, balance sheets, cash flow, AR/AP aging — generated on demand.", i: "M3 3v18h18 M7 14l4-4 4 4 6-6" },
  { t: "Swagger Docs", d: "Interactive API documentation included out of the box.", i: "M4 19.5A2.5 2.5 0 016.5 17H20 M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" },
  { t: "JWT + Cookie Auth", d: "Two auth methods supported with full session management.", i: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
];

const modules = [
  { n: "Authentication", d: "User accounts & sessions" },
  { n: "Accounting", d: "Core ledger operations" },
  { n: "Chart of Accounts", d: "Account hierarchy management" },
  { n: "Customers & Vendors", d: "Contact management" },
  { n: "Invoices & Bills", d: "Receivables & payables" },
  { n: "Payments", d: "Record & match payments" },
  { n: "Bank Accounts & Cards", d: "Banking integrations" },
  { n: "Inventory", d: "Stock & item tracking" },
  { n: "Tax Management", d: "Tax codes & rates" },
  { n: "Payroll", d: "Employees & runs" },
  { n: "Financial Reports", d: "P&L, BS, cash flow & aging" },
];



function Homepage() {
  return (
    <div className="bg-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-line">
        <div className="max-w-[1560px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link className="flex items-center gap-2.5 font-bold" to="/">
            <span className="w-[30px] h-[30px] rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-[13px] font-extrabold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]">Q</span>
            <span>QuickBook Connector</span>
          </Link>
          <nav className="flex items-center gap-2">
            <a className="hidden md:block px-3 py-2 text-muted font-medium text-sm rounded-lg hover:text-ink hover:bg-soft" href="#features">Features</a>
            <a className="hidden md:block px-3 py-2 text-muted font-medium text-sm rounded-lg hover:text-ink hover:bg-soft" href="https://qbo1.api.xcelerateai-api.com/api-docs/">Docs</a>
            <Link className="px-3 py-2 text-muted font-medium text-sm rounded-lg hover:text-ink hover:bg-soft" to="/contact-us">Contact</Link>
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden py-24 md:py-32 bg-[radial-gradient(900px_500px_at_85%_-10%,rgba(99,102,241,0.10),transparent_60%),radial-gradient(600px_400px_at_10%_0%,rgba(58,71,255,0.08),transparent_60%)] before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(to_right,rgba(10,21,48,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(10,21,48,0.05)_1px,transparent_1px)] before:bg-[length:40px_40px] before:[mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,#000_30%,transparent_75%)] before:pointer-events-none">
        <div className="max-w-[820px] mx-auto px-6 relative text-center">
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-b from-navy to-[#1f2a55] bg-clip-text text-transparent leading-tight">Connect Your App to QuickBooks in Minutes</h1>
          <p className="mt-5 text-lg text-muted max-w-[660px] mx-auto">
            A production-ready OAuth 2.0 server that handles QuickBooks authentication,
            token management, and all major accounting operations — so you don't have to.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <a className="inline-flex items-center gap-2 h-12 px-6 rounded-xl font-semibold text-[15px] bg-white text-ink border border-line hover:border-[#c9cfdb] hover:bg-soft transition-all" href="https://qbo1.api.xcelerateai-api.com/api-docs/">View Documentation</a>
            <Link className="inline-flex items-center gap-2 h-12 px-6 rounded-xl font-semibold text-[15px] bg-gradient-to-br from-navy to-indigo text-white hover:shadow-lg transition-all" to="/contact-us">Contact Us</Link>
          </div>
          <p className="mt-6 text-[13px] text-muted">Built for developers <span className="mx-1.5 opacity-50">·</span> REST API</p>
        </div>
      </section>

      <section className="py-20 border-t border-line" id="features">
        <div className="max-w-[1160px] mx-auto px-6 ">
          <div className="text-center max-w-[680px] mx-auto mb-12">
            <span className="inline-block text-indigo-2 font-semibold text-[13px] uppercase tracking-wider mb-3">Features</span>
            <h2 className="text-3xl md:text-4xl font-bold">Everything you need to ship QuickBooks integration</h2>
            <p className="mt-3.5 text-muted text-base">Battle-tested primitives, sensible defaults, and a clean REST surface.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <div key={f.t} className="p-7 bg-white border border-line rounded-custom hover:border-[#c9cfdb] hover:-translate-y-0.5 hover:shadow-custom transition-all">
                <div className="w-[42px] h-[42px] rounded-[10px] bg-gradient-to-br from-[#eef0ff] to-[#e0e4ff] grid place-items-center text-navy mb-4.5"><Icon d={f.i} /></div>
                <h3 className="text-[17px] font-semibold mb-2">{f.t}</h3>
                <p className="text-muted text-[14.5px]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-line">
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="text-center max-w-[680px] mx-auto mb-12">
            <span className="inline-block text-indigo-2 font-semibold text-[13px] uppercase tracking-wider mb-3">How it works</span>
            <h2 className="text-3xl md:text-4xl font-bold">Three steps to live data</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: "Register & Login", d: "Create a user account on the server with email and password." },
              { t: "Connect QuickBooks", d: "Authorize via OAuth 2.0. Tokens are stored and refreshed automatically." },
              { t: "Make API Calls", d: "Use REST endpoints to access invoices, customers, reports, and more." },
            ].map((s, i) => (
              <div key={s.t} className="p-7 bg-soft rounded-custom">
                <div className="w-8 h-8 rounded-full bg-navy text-white grid place-items-center font-bold text-sm mb-4">{i + 1}</div>
                <h3 className="text-[17px] mb-1.5">{s.t}</h3>
                <p className="text-muted text-[14.5px]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-line">
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="text-center max-w-[680px] mx-auto mb-12">
            <span className="inline-block text-indigo-2 font-semibold text-[13px] uppercase tracking-wider mb-3">API surface</span>
            <h2 className="text-3xl md:text-4xl font-bold">11 modules covering the full QuickBooks API</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modules.map((m) => (
              <div key={m.n} className="flex items-center gap-3.5 p-5 bg-white border border-line rounded-xl hover:border-indigo-2 hover:bg-[#fafbff] transition-all">
                <div className="w-[38px] h-[38px] rounded-[10px] bg-soft grid place-items-center text-navy flex-shrink-0"><Icon d="M9 11l3 3L22 4 M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></div>
                <div>
                  <div className="font-semibold text-[15px]">{m.n}</div>
                  <div className="text-[13px] text-muted mt-0.5">{m.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="py-20 border-t border-line">
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="text-center max-w-[680px] mx-auto mb-12">
            <span className="inline-block text-indigo-2 font-semibold text-[13px] uppercase tracking-wider mb-3">Tech stack</span>
            <h2 className="text-3xl md:text-4xl font-bold">Built on tools you already trust</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-2.5 max-w-[760px] mx-auto">
            {stack.map((s) => (
              <span key={s} className="px-4 py-2 border border-line rounded-full bg-white text-[13.5px] font-medium text-ink">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mr-2 align-middle" />{s}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-line" id="start">
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="text-center max-w-[680px] mx-auto mb-12">
            <span className="inline-block text-indigo-2 font-semibold text-[13px] uppercase tracking-wider mb-3">Quick start</span>
            <h2 className="text-3xl md:text-4xl font-bold">From zero to your first request</h2>
            <p className="mt-3.5 text-muted text-base">Spin up the server, register a user, and pull live company data.</p>
          </div>
          <div className="max-w-[880px] mx-auto rounded-custom overflow-hidden border border-[#1b2440] shadow-[0_20px_50px_-20px_rgba(10,21,48,0.35)]">
            <div className="bg-[#070b18] px-4 py-3 flex items-center gap-2 border-b border-[#1b2440]">
              <span className="w-[11px] h-[11px] rounded-full bg-[#ff5f57]" />
              <span className="w-[11px] h-[11px] rounded-full bg-[#febc2e]" />
              <span className="w-[11px] h-[11px] rounded-full bg-[#28c840]" />
              <span className="ml-3 text-[#9aa4b8] text-[12.5px] font-mono">terminal — bash</span>
            </div>
            <pre className="m-0 bg-[#0b1020] text-[#e6edf3] p-6 overflow-auto font-mono text-[13.5px] leading-relaxed"><code dangerouslySetInnerHTML={{ __html:
`<span class="text-[#7d8bb1]"># Connect to QuickBooks</span>
<span class="text-[#ff7b72]">curl</span> -X <span class="text-[#a5d6ff]">'GET'</span> \\
  <span class="text-[#a5d6ff]">'https://api-xcelerateai.demotrt.com/auth/connect?user_id=testg%40gmail.com'</span> \\
  -H <span class="text-[#a5d6ff]">'accept: application/json'</span>

<span class="text-[#7d8bb1]"># Get company info</span>
<span class="text-[#ff7b72]">curl</span> -X <span class="text-[#a5d6ff]">'GET'</span> \\
  <span class="text-[#a5d6ff]">'https://api-xcelerateai.demotrt.com/api/quickbooks/company-info?organizationId=&lt;organization-id&gt;'</span> \\
  -H <span class="text-[#a5d6ff]">'Authorization: Bearer &lt;your-token&gt;'</span>`
            }} /></pre>
          </div>
        </div>
      </section> */}

      <section className="py-20 border-t border-line">
        <div className="max-w-[1160px] mx-auto px-6">
          <div className="py-14 bg-gradient-to-br from-navy to-[#1a2654] rounded-3xl py-18 px-8 text-center text-white relative overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] before:bg-[length:32px_32px] before:[mask-image:radial-gradient(ellipse_at_center,#000_30%,transparent_75%)]">
            <h2 className="text-3xl text-[#fff] md:text-4xl font-bold relative">Ready to integrate QuickBooks?</h2>
            <p className="relative text-[#aab3d1] mt-3 text-base">Get started with your QuickBooks integration today.</p>
            <div className="relative mt-7 flex justify-center gap-3 flex-wrap">

              <a className="inline-flex items-center gap-2 h-12 px-6 rounded-xl font-semibold text-[15px] bg-transparent text-white border border-white/25 hover:bg-white/8 hover:border-white/50 transition-all" href="https://qbo1.api.xcelerateai-api.com/api-docs/" target="_blank" rel="noreferrer">View API Docs</a>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-14 border-t border-line mt-22">
        <div className="max-w-[1560px] mx-auto px-6">
          <div className="flex justify-between gap-8 flex-wrap items-start">
            <div className="max-w-[340px]">
              <div className="flex items-center gap-2.5 font-bold mb-2.5">
                <span className="w-[30px] h-[30px] rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-[13px] font-extrabold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]">Q</span>
                <span>QuickBook Connector</span>
              </div>
              <p className="text-muted text-sm">The missing OAuth + accounting API layer between your app and QuickBooks Online.</p>
            </div>
            <div className="flex gap-7 flex-wrap">
              <a href="https://qbo1.api.xcelerateai-api.com/api-docs/" target="_blank" rel="noreferrer" className="text-muted text-sm hover:text-ink">Documentation</a>
              <Link to="/privacy" className="text-muted text-sm hover:text-ink">Privacy Policy</Link>
              <Link to="/terms" className="text-muted text-sm hover:text-ink">Terms of Service</Link>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-line text-[13px] text-muted flex justify-between flex-wrap gap-2">
            <span>© {new Date().getFullYear()} QuickBook Connector</span>
            <span>Built for QuickBooks integration</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Homepage;
